/**
 * VendorConfirmDrawer (S3.2) — capture one A1/A2/A3 vendor checkpoint
 * with the per-line 黃單 counter-sign fields (交貨日 / 數量 / 價格調整 /
 * 箱數 / 裝箱).
 *
 * - Checkpoint labels ALWAYS render bilingual from `ofcVocabulary.ts`
 *   (A1 估計 / A2 出貨通知再確認 / A3 裝櫃實際) — never bare codes.
 * - Submitting mutates the confirmed OCC quantity buckets through the
 *   useVendorConfirm orchestrator; short/over/delay lines raise an OCC
 *   訂單異常 routed to its owner (late→採購 · short-ship→船務) and the
 *   capture lands in the poStore checkpoint log.
 * - RHF + zodResolver (`mode: 'onTouched'`), 44px controls, live
 *   short/over/delay preview per line (icon + numbers, never colour alone).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Row,
  Segmented,
  Switch,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { extractApiError } from '@/hooks/useSalesOrders';
import { classifyCheckpointException, useVendorConfirm } from '@/hooks/usePurchaseOrders';
import type { OccLine } from '@/types/api/occ';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { VendorCheckpointCode } from '@/constants/ofcVocabulary';
import type { VendorCheckpointRecord } from '@/stores/poStore';
import { WarningOutlined } from '@ant-design/icons';
import {
  CHECKPOINT_BUCKET,
  CHECKPOINT_CODES,
  deriveCheckpointStatuses,
  nextCheckpoint,
  occAllocationForPoLine,
  useCheckpointLabel,
} from './PurchaseOrderDetailPage/checkpoints';

const { Text } = Typography;

interface VendorConfirmDrawerProps {
  open: boolean;
  po: PurchaseOrder;
  occLines: OccLine[];
  /** Checkpoint records already captured for THIS po. */
  records: VendorCheckpointRecord[];
  onClose: () => void;
  /** Called after a successful capture (page refreshes its reads). */
  onSaved: () => void;
}

interface ConfirmLineFormValues {
  poLineId: string;
  qtyConfirmed: number;
  deliveryDate: string | null;
  priceAdjusted: boolean;
  boxCount: number | null;
  packaging: string;
}

interface ConfirmFormValues {
  note: string;
  lines: ConfirmLineFormValues[];
}

const VendorConfirmDrawer = ({
  open,
  po,
  occLines,
  records,
  onClose,
  onSaved,
}: VendorConfirmDrawerProps) => {
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const cpLabel = useCheckpointLabel();
  const { confirmCheckpoint } = useVendorConfirm();

  const statuses = useMemo(
    () => deriveCheckpointStatuses(po, occLines, records),
    [po, occLines, records]
  );

  const [checkpoint, setCheckpoint] = useState<VendorCheckpointCode>('A1');
  useEffect(() => {
    if (open) setCheckpoint(nextCheckpoint(statuses));
    // statuses is derived from the same reads the page refreshes on save —
    // re-picking while the drawer stays closed is a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Baseline a line is judged against for the chosen checkpoint. */
  const baselineFor = (poLineId: string, qty: number): number => {
    const alloc = occAllocationForPoLine(occLines, poLineId);
    if (checkpoint === 'A3' && alloc) {
      return alloc.qtyNoticeConfirmed || alloc.qtyEstimated || qty;
    }
    return qty;
  };

  const schema = useMemo(
    () =>
      z.object({
        note: z.string(),
        lines: z.array(
          z.object({
            poLineId: z.string(),
            qtyConfirmed: z
              .number({
                required_error: t('procurement:confirmDrawer.validation.qtyRequired'),
                invalid_type_error: t('procurement:confirmDrawer.validation.qtyRequired'),
              })
              .min(0, t('procurement:confirmDrawer.validation.qtyMin')),
            deliveryDate: z.string().nullable(),
            priceAdjusted: z.boolean(),
            boxCount: z.number().nullable(),
            packaging: z.string(),
          })
        ),
      }),
    [t]
  );

  const defaultFormValues = useMemo<ConfirmFormValues>(
    () => ({ note: '', lines: [] }),
    []
  );

  const formValues = useMemo<ConfirmFormValues>(() => {
    if (!open) return defaultFormValues;
    return {
      note: '',
      lines: po.lines.map((line) => {
        const alloc = occAllocationForPoLine(occLines, line.id);
        const recordedQty = alloc?.[CHECKPOINT_BUCKET[checkpoint]] ?? 0;
        return {
          poLineId: line.id,
          qtyConfirmed: recordedQty > 0 ? recordedQty : baselineFor(line.id, line.qty),
          deliveryDate: line.expectedDate ?? po.expectedDeliveryDate ?? null,
          priceAdjusted: false,
          boxCount: null,
          packaging: '',
        };
      }),
    };
    // baselineFor depends on checkpoint + occLines, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, checkpoint, po, occLines, defaultFormValues]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields },
  } = useForm<ConfirmFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultFormValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const watchedLines = useWatch({ control, name: 'lines' }) ?? [];

  const previewFor = (index: number) => {
    const line = po.lines[index];
    const watched = watchedLines[index];
    if (!line || !watched || typeof watched.qtyConfirmed !== 'number') return null;
    const referenceDate = line.expectedDate ?? po.expectedDeliveryDate ?? null;
    return classifyCheckpointException(
      checkpoint,
      baselineFor(line.id, line.qty),
      watched.qtyConfirmed,
      watched.deliveryDate ?? null,
      referenceDate
    );
  };

  const alreadyRecorded = statuses.find((status) => status.code === checkpoint)?.recorded;

  const submit = async (data: ConfirmFormValues) => {
    try {
      const record = await confirmCheckpoint(po, occLines, {
        checkpoint,
        note: data.note,
        lines: data.lines.map((line) => ({
          poLineId: line.poLineId,
          qtyConfirmed: line.qtyConfirmed,
          deliveryDate: line.deliveryDate,
          priceAdjusted: line.priceAdjusted,
          boxCount: line.boxCount,
          packaging: line.packaging.trim() || null,
        })),
      });
      const exceptionLine = record.lines.find((line) => line.exception);
      if (exceptionLine?.exception) {
        message.warning(
          t('procurement:messages.checkpointException', {
            checkpoint: cpLabel(checkpoint),
            owner: t(`procurement:owner.${exceptionLine.exception.owner}`),
          })
        );
      } else {
        message.success(
          t('procurement:messages.checkpointSaved', { checkpoint: cpLabel(checkpoint) })
        );
      }
      onSaved();
      onClose();
    } catch (error) {
      const { message: detail } = extractApiError(error);
      message.error(detail || t('common:messages.updateFailed'));
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      title={t('procurement:confirmDrawer.title')}
      destroyOnClose
      footer={
        <div className="tw-flex tw-justify-end tw-gap-3">
          <Button
            onClick={onClose}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isSubmitting}
            onClick={handleSubmit(submit)}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('procurement:confirmDrawer.submit')}
          </Button>
        </div>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('procurement:confirmDrawer.subtitle', { poNumber: po.poNumber })}
      </Text>

      {/* Checkpoint picker — sanctioned bilingual labels only */}
      <Text style={formStyles.labelStyle}>{t('procurement:confirmDrawer.checkpointLabel')}</Text>
      <Segmented
        block
        size="large"
        value={checkpoint}
        onChange={(value) => setCheckpoint(value as VendorCheckpointCode)}
        options={CHECKPOINT_CODES.map((code) => ({ value: code, label: cpLabel(code) }))}
        style={{ marginBottom: token.marginMD }}
      />

      {alreadyRecorded && (
        <Alert
          className="tw-mb-4"
          type="info"
          showIcon
          message={t('procurement:confirmDrawer.alreadyRecorded')}
        />
      )}

      {/* Per-line 黃單 capture */}
      {po.lines.map((line, index) => {
        const preview = previewFor(index);
        const lineErrors = errors.lines?.[index];
        const lineTouched = touchedFields.lines?.[index];
        return (
          <div key={line.id} style={formStyles.fieldContainerStyle}>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-flex-wrap">
              <Text style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</Text>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {line.productName}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: token.fontSizeSM, marginLeft: 'auto' }}
              >
                {t('procurement:detail.checkpoints.ordered')}:{' '}
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {baselineFor(line.id, line.qty)}
                </span>
              </Text>
            </div>
            <Row gutter={[16, 12]}>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('procurement:confirmDrawer.qtyConfirmed')}
                </Text>
                <Controller
                  name={`lines.${index}.qtyConfirmed`}
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      onBlur={field.onBlur}
                      min={0}
                      size="large"
                      style={formStyles.inputStyle}
                      status={lineErrors?.qtyConfirmed ? 'error' : undefined}
                    />
                  )}
                />
                {lineErrors?.qtyConfirmed && lineTouched?.qtyConfirmed && (
                  <Text style={formStyles.errorStyle}>
                    {lineErrors.qtyConfirmed.message ?? ''}
                  </Text>
                )}
              </Col>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>
                  {t('procurement:confirmDrawer.deliveryDate')}
                </Text>
                <Controller
                  name={`lines.${index}.deliveryDate`}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) =>
                        field.onChange(value ? value.format('YYYY-MM-DD') : null)
                      }
                      onBlur={field.onBlur}
                      size="large"
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>
                  {t('procurement:confirmDrawer.boxCount')}
                </Text>
                <Controller
                  name={`lines.${index}.boxCount`}
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      onBlur={field.onBlur}
                      min={0}
                      size="large"
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>
                  {t('procurement:confirmDrawer.priceAdjusted')}
                </Text>
                <Controller
                  name={`lines.${index}.priceAdjusted`}
                  control={control}
                  render={({ field }) => (
                    <Switch checked={field.value} onChange={field.onChange} />
                  )}
                />
              </Col>
              <Col span={16}>
                <Text style={formStyles.labelStyle}>
                  {t('procurement:confirmDrawer.packaging')}
                </Text>
                <Controller
                  name={`lines.${index}.packaging`}
                  control={control}
                  render={({ field }) => (
                    <Input {...field} size="large" style={formStyles.inputStyle} />
                  )}
                />
              </Col>
            </Row>
            {/* Live short/over/delay preview — icon + numbers, never colour alone */}
            <div style={{ marginTop: '8px' }}>
              {preview ? (
                <Text
                  className="tw-inline-flex tw-items-center tw-gap-1"
                  style={{ color: token.colorWarning, fontSize: token.fontSizeSM }}
                >
                  <WarningOutlined />
                  {t(`procurement:confirmDrawer.preview.${preview.kind}`, {
                    qty: preview.gap,
                    owner: t(`procurement:owner.${preview.owner}`),
                  })}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t('procurement:confirmDrawer.preview.ok')}
                </Text>
              )}
            </div>
          </div>
        );
      })}

      {/* 黃單 note */}
      <Text style={formStyles.labelStyle}>{t('procurement:confirmDrawer.noteLabel')}</Text>
      <Controller
        name="note"
        control={control}
        render={({ field }) => (
          <Input.TextArea
            {...field}
            rows={3}
            placeholder={t('procurement:confirmDrawer.notePlaceholder')}
            style={{ ...formStyles.inputStyle, height: 'auto' }}
          />
        )}
      />
    </Drawer>
  );
};

export default VendorConfirmDrawer;
